// frontend/src/pages/Reports/SummaryServiceReportEdit.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Card, Typography, Button, Table, Spin, Tag, Space, 
  Modal, Form, Select, Input, message, Popconfirm, Alert,
  Tooltip, Checkbox, Row, Col, Divider, Radio, Popover, Dropdown, Menu
} from 'antd';
import { 
  SaveOutlined, ArrowLeftOutlined, CopyOutlined, DeleteOutlined,
  CheckCircleOutlined, ClockCircleOutlined, EditOutlined,
  FileExcelOutlined, SignatureOutlined, SendOutlined, EyeOutlined,
  DownloadOutlined, FilePdfOutlined, CalendarOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import api from '../../utils/api';
import '../Analytics/Analytics.css';

const { Title, Text } = Typography;

// Product options from legend - memoized outside component
const PRODUCT_OPTIONS = [
  'DMSALL', 'VXRAIL', 'ISI', 'DD', 'Win OS', 'VM', 'PS', 
  'AX', 'NW', 'DMS', 'SIC', 'UAC', 'DMSAPI', 'SQL', 'SS', 'HHS', 'ALL'
];

// Service Type options
const SERVICE_TYPE_OPTIONS = [
  'Support', 'Health Check', 'Upgrade', 'PM', 'CM', 'DR', 'Deployment/Implementation'
];

// Location options
const LOCATION_OPTIONS = [
  'LHDNM Cyberjaya', 'LHDNM Melaka'
];

// Helper functions
const getShortLocationName = (name) => {
  if (name === 'LHDNM Cyberjaya') return 'CBJ';
  if (name === 'LHDNM Melaka') return 'MLK';
  return name;
};

const getLocationShortCode = (location) => {
  if (location === 'LHDNM Cyberjaya') return 'CBJ';
  if (location === 'LHDNM Melaka') return 'MLK';
  return location;
};

// Memoized Select options
const productSelectOptions = PRODUCT_OPTIONS.map(opt => ({ label: opt, value: opt }));
const serviceTypeSelectOptions = SERVICE_TYPE_OPTIONS.map(opt => ({ label: opt, value: opt }));
const locationSelectOptions = LOCATION_OPTIONS.map(opt => ({ label: opt, value: opt }));

const SummaryServiceReportEdit = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const notificationApi = useNotificationApi();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [pdfPreviewBuffer, setPdfPreviewBuffer] = useState(null);
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');
  const [report, setReport] = useState(null);
  const [dailyEntries, setDailyEntries] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [signSubmitModalVisible, setSignSubmitModalVisible] = useState(false);
  const [batchForm] = Form.useForm();
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Weekend management states
  const [missingWeekendsModalVisible, setMissingWeekendsModalVisible] = useState(false);
  const [missingWeekends, setMissingWeekends] = useState([]);
  const [selectedWeekends, setSelectedWeekends] = useState([]);
  const [loadingWeekends, setLoadingWeekends] = useState(false);
  
  // Assignment states for Sign & Submit modal
  const [selectedSupervisorId, setSelectedSupervisorId] = useState(null);
  const [selectedCustomerSupervisorId, setSelectedCustomerSupervisorId] = useState(null);
  const [potentialSupervisors, setPotentialSupervisors] = useState([]);
  const [customerSupervisors, setCustomerSupervisors] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Memoized team member options
  const teamMemberOptions = useMemo(() => {
    return teamMembers.map(member => ({ 
      label: `${member.full_name} (${member.user_type === 'internal_staff' ? 'Internal' : 'Partner'})`, 
      value: member.full_name 
    }));
  }, [teamMembers]);

  // Memoized supervisor options (ANY internal or partner user)
  const supervisorOptions = useMemo(() => {
    return potentialSupervisors.map(sup => ({ 
      label: `${sup.firstName} ${sup.lastName}`,
      value: sup.id 
    }));
  }, [potentialSupervisors]);

  // Memoized customer supervisor options (ONLY Customer Supervisor role)
  const customerSupervisorOptions = useMemo(() => {
    return customerSupervisors.map(cust => ({ 
      label: `${cust.firstName} ${cust.lastName}`,
      value: cust.id 
    }));
  }, [customerSupervisors]);

  useEffect(() => {
    fetchReport();
    fetchTeamMembers();
  }, [id]);

  // Fetch potential supervisors and customer supervisors when report is loaded
  useEffect(() => {
    if (report?.id) {
      fetchPotentialSupervisors();
      fetchCustomerSupervisors();
    }
  }, [report?.id]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/reports/summary-service-report/${id}`);
      if (response.data.success) {
        const reportData = response.data.data.report;
        setReport(reportData);
        setDailyEntries(response.data.data.dailyEntries || []);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      notificationApi.error({
        message: 'Error',
        description: 'Failed to load report',
      });
      navigate('/reports/summary-service-report');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/api/reports/summary-service-report/team-members');
      if (response.data.success) {
        setTeamMembers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  // Fetch potential supervisors (System Admin, Internal Staff Supervisor, and Partner Supervisor roles)
  const fetchPotentialSupervisors = async () => {
    setLoadingSupervisors(true);
    try {
      console.log('Fetching supervisors for selection (System Admin, Internal Staff Supervisor, Partner Supervisor)');
      
      const internalResponse = await api.get('/api/users', {
        params: {
          userType: 'internal_staff',
          isActive: true,
          sort: 'firstName',
          order: 'ASC'
        }
      });
      
      const partnerResponse = await api.get('/api/users', {
        params: {
          userType: 'partner',
          isActive: true,
          sort: 'firstName',
          order: 'ASC'
        }
      });
      
      let allUsers = [];
      if (internalResponse.data.success) {
        allUsers = [...allUsers, ...internalResponse.data.data];
      }
      if (partnerResponse.data.success) {
        allUsers = [...allUsers, ...partnerResponse.data.data];
      }
      
      const supervisors = allUsers.filter(
        user => (user.roleId === 1 || user.roleId === 2 || user.roleId === 4) &&
                user.id !== report?.prepared_by_user_id
      );
      console.log('Supervisors found:', supervisors.length);
      setPotentialSupervisors(supervisors);
    } catch (error) {
      console.error('Error fetching supervisors:', error);
      notificationApi.error({
        message: 'Error',
        description: 'Failed to load supervisors',
      });
    } finally {
      setLoadingSupervisors(false);
    }
  };

  // Fetch customer supervisors (ONLY users with Customer Supervisor role - role_id = 6)
  const fetchCustomerSupervisors = async () => {
    setLoadingCustomers(true);
    try {
      const response = await api.get('/api/users', {
        params: {
          userType: 'customer',
          isActive: true,
          sort: 'firstName',
          order: 'ASC'
        }
      });
      
      if (response.data.success) {
        const customerSupervisorsList = response.data.data.filter(
          user => user.roleId === 6
        );
        console.log('Customer supervisors found:', customerSupervisorsList.length);
        setCustomerSupervisors(customerSupervisorsList);
      }
    } catch (error) {
      console.error('Error fetching customer supervisors:', error);
      notificationApi.error({
        message: 'Error',
        description: 'Failed to load customer supervisors',
      });
    } finally {
      setLoadingCustomers(false);
    }
  };

  const updateDailyEntry = useCallback(async (dailyId, field, value) => {
    try {
      await api.put(`/api/reports/summary-service-report/${id}/daily/${dailyId}`, {
        [field]: value
      });
    } catch (error) {
      console.error('Error updating entry:', error);
      throw error;
    }
  }, [id]);

  const handleCellChange = useCallback((dailyId, field, value) => {
    setDailyEntries(prev => prev.map(entry => 
      entry.id === dailyId ? { ...entry, [field]: value } : entry
    ));
    
    updateDailyEntry(dailyId, field, value).catch(() => {
      fetchReport();
      notificationApi.error({
        message: 'Error',
        description: 'Failed to save changes',
      });
    });
  }, [updateDailyEntry, notificationApi, fetchReport]);

  const handleBatchUpdate = useCallback(async () => {
    const values = await batchForm.validateFields();
    
    const updates = selectedRowKeys.map(dailyId => {
      const update = { dailyId };
      
      if (values.service_report_no && values.service_report_no.trim() !== '') {
        update.service_report_no = values.service_report_no;
      }
      if (values.products && values.products.length > 0) {
        update.products = values.products.join(' & ');
      }
      if (values.service_types && values.service_types.length > 0) {
        update.service_types = values.service_types.join(' & ');
      }
      if (values.location && values.location.length > 0) {
        update.location = values.location.map(getLocationShortCode).join(' & ');
      }
      if (values.engineer_name && values.engineer_name.length > 0) {
        update.engineer_name = values.engineer_name.join(' & ');
      }
      
      return update;
    });
    
    setSaving(true);
    try {
      await api.put(`/api/reports/summary-service-report/${id}/batch-update`, { updates });
      notificationApi.success({
        message: 'Success',
        description: `${updates.length} entries updated successfully`,
      });
      setBatchModalVisible(false);
      setSelectedRowKeys([]);
      fetchReport();
    } catch (error) {
      console.error('Error batch updating:', error);
      notificationApi.error({
        message: 'Error',
        description: 'Failed to update entries',
      });
    } finally {
      setSaving(false);
    }
  }, [id, selectedRowKeys, batchForm, notificationApi, fetchReport]);

  const handleCopyRow = useCallback(async (dailyId) => {
    try {
      const response = await api.post(`/api/reports/summary-service-report/${id}/copy-entry/${dailyId}`);
      if (response.data.success) {
        notificationApi.success({
          message: 'Success',
          description: 'Row copied successfully',
        });
        fetchReport();
      }
    } catch (error) {
      console.error('Error copying row:', error);
      notificationApi.error({
        message: 'Error',
        description: 'Failed to copy row',
      });
    }
  }, [id, notificationApi, fetchReport]);

  const handleBulkCopy = useCallback(async (dailyId) => {
    try {
      const response = await api.post(`/api/reports/summary-service-report/${id}/bulk-copy/${dailyId}`);
      if (response.data.success) {
        notificationApi.success({
          message: 'Success',
          description: response.data.message,
        });
        fetchReport();
      }
    } catch (error) {
      console.error('Error in bulk copy:', error);
      notificationApi.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to copy values to empty rows',
      });
    }
  }, [id, notificationApi, fetchReport]);

  const handleClearRow = useCallback(async (dailyId) => {
    try {
      const response = await api.put(`/api/reports/summary-service-report/${id}/daily/${dailyId}/clear`);
      if (response.data.success) {
        notificationApi.success({
          message: 'Success',
          description: 'Row cleared successfully',
        });
        fetchReport();
      }
    } catch (error) {
      console.error('Error clearing row:', error);
      notificationApi.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to clear row',
      });
    }
  }, [id, notificationApi, fetchReport]);

  // Delete a single row from the report
  const handleDeleteRow = useCallback(async (dailyId, itemNumber) => {
    setSaving(true);
    try {
      const response = await api.delete(`/api/reports/summary-service-report/${id}/daily/${dailyId}`);
      if (response.data.success) {
        notificationApi.success({
          message: 'Success',
          description: 'Row deleted successfully. Remaining rows have been reordered.',
        });
        fetchReport(); // Refresh the table
      }
    } catch (error) {
      console.error('Error deleting row:', error);
      notificationApi.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to delete row',
      });
    } finally {
      setSaving(false);
    }
  }, [id, notificationApi, fetchReport]);

  // Fetch missing weekends for the report
  const fetchMissingWeekends = useCallback(async () => {
    setLoadingWeekends(true);
    try {
      const response = await api.get(`/api/reports/summary-service-report/${id}/missing-weekends`);
      if (response.data.success) {
        setMissingWeekends(response.data.data.missingWeekends);
        setSelectedWeekends([]);
        setMissingWeekendsModalVisible(true);
      }
    } catch (error) {
      console.error('Error fetching missing weekends:', error);
      notificationApi.error({
        message: 'Error',
        description: 'Failed to load available weekends',
      });
    } finally {
      setLoadingWeekends(false);
    }
  }, [id, notificationApi]);

  // Add selected weekends to the report
  const handleAddWeekends = useCallback(async () => {
    if (selectedWeekends.length === 0) {
      notificationApi.warning({
        message: 'No Selection',
        description: 'Please select at least one weekend to add.',
      });
      return;
    }

    setLoadingWeekends(true);
    try {
      const response = await api.post(`/api/reports/summary-service-report/${id}/add-weekends`, {
        dates: selectedWeekends
      });
      
      if (response.data.success) {
        notificationApi.success({
          message: 'Success',
          description: response.data.message || `${selectedWeekends.length} weekend(s) added successfully.`,
        });
        
        // Refresh the report to show new rows
        await fetchReport();
        setMissingWeekendsModalVisible(false);
        setSelectedWeekends([]);
      }
    } catch (error) {
      console.error('Error adding weekends:', error);
      notificationApi.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to add weekends',
      });
    } finally {
      setLoadingWeekends(false);
    }
  }, [id, selectedWeekends, notificationApi, fetchReport]);

  // Handle weekend checkbox selection
  const handleWeekendSelect = useCallback((date, checked) => {
    if (checked) {
      setSelectedWeekends(prev => [...prev, date]);
    } else {
      setSelectedWeekends(prev => prev.filter(d => d !== date));
    }
  }, []);

  // Helper function to download file with authentication token using POST
  const downloadFile = useCallback(async (url, filename = null) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      notificationApi.error({
        message: 'Error',
        description: 'You are not authenticated. Please login again.',
      });
      return;
    }
    
    try {
      const fullUrl = url;
      console.log('[Download] Full URL:', fullUrl);
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Get filename from Content-Disposition header - improved parsing
      let downloadFilename = filename;
      const contentDisposition = response.headers.get('Content-Disposition');
      console.log('[Download] Content-Disposition:', contentDisposition);
      
      if (contentDisposition && !downloadFilename) {
        // Try to match filename*=UTF-8''... format first (RFC 5987)
        let match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (match && match[1]) {
          downloadFilename = decodeURIComponent(match[1]);
        } else {
          // Try to match filename="..." format
          match = contentDisposition.match(/filename="([^"]+)"/i);
          if (match && match[1]) {
            downloadFilename = match[1];
          } else {
            // Try simple filename= format
            match = contentDisposition.match(/filename=([^;]+)/i);
            if (match && match[1]) {
              downloadFilename = match[1].replace(/['"]/g, '');
            }
          }
        }
      }
      
      // Determine file extension from URL if still no filename
      if (!downloadFilename) {
        const isPdf = url.includes('format=pdf');
        downloadFilename = isPdf ? 'report.pdf' : 'report.xlsx';
      }
      
      console.log('[Download] Final filename:', downloadFilename);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      
      notificationApi.success({
        message: 'Success',
        description: 'Download started',
      });
    } catch (error) {
      console.error('[Download] Error:', error);
      notificationApi.error({
        message: 'Error',
        description: error.message || 'Failed to download file',
      });
    }
  }, [notificationApi]);

  // Download as Excel
  const handleDownloadExcel = useCallback(async () => {
    setExporting(true);
    try {
      await downloadFile(`/api/reports/summary-service-report/${id}/export-excel?format=xlsx`);
    } catch (error) {
      console.error('Download Excel failed:', error);
    } finally {
      setExporting(false);
    }
  }, [id, downloadFile]);

  // Download as PDF
  const handleDownloadPdf = useCallback(async () => {
    setExporting(true);
    try {
      await downloadFile(`/api/reports/summary-service-report/${id}/export-excel?format=pdf`);
    } catch (error) {
      console.error('Download PDF failed:', error);
    } finally {
      setExporting(false);
    }
  }, [id, downloadFile]);

  const handlePreviewReport = useCallback(async () => {
    setPreviewing(true);
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        notificationApi.error({
          message: 'Error',
          description: 'You are not authenticated. Please login again.',
        });
        setPreviewing(false);
        return;
      }
      
      const hideLoading = message.loading('Generating preview...', 0);
      
      // Use relative URL (production-friendly)
      const fullUrl = `/api/reports/summary-service-report/${id}/export-excel?format=pdf&token=${encodeURIComponent(token)}`;
      console.log('[Preview] Full URL:', fullUrl);
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      hideLoading();
      
      if (!response.ok) {
        throw new Error(`Preview failed with status ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      
      // Check if it's a PDF
      const bytes = new Uint8Array(arrayBuffer.slice(0, 4));
      const isPdf = bytes[0] === 37 && bytes[1] === 80 && bytes[2] === 68 && bytes[3] === 70;
      
      if (!isPdf) {
        const textDecoder = new TextDecoder();
        const text = textDecoder.decode(arrayBuffer.slice(0, 200));
        console.error('Response is not PDF:', text);
        throw new Error('Server did not return a PDF');
      }
      
      setPdfPreviewBuffer(arrayBuffer);
      setPdfPreviewTitle(`Summary Service Report - ${report?.contract_name || 'Report'}`);
      setPreviewModalVisible(true);
      
      notificationApi.success({
        message: 'Success',
        description: 'Report preview ready',
      });
      
    } catch (error) {
      console.error('[Preview] Error:', error);
      notificationApi.error({
        message: 'Error',
        description: error.message || 'Failed to preview report',
      });
    } finally {
      setPreviewing(false);
    }
  }, [id, report, notificationApi]);

  // Preview modal close handler
  const handlePreviewModalClose = useCallback(() => {
    setPreviewModalVisible(false);
    setPdfPreviewBuffer(null);
    setPdfPreviewTitle('');
  }, []);

  // Dropdown menu for download options
  const downloadMenuItems = [
    {
      key: 'excel',
      icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
      label: 'Download as Excel (.xlsx)',
      onClick: handleDownloadExcel,
    },
    {
      key: 'pdf',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      label: 'Download as PDF (.pdf)',
      onClick: handleDownloadPdf,
    },
  ];

  const handleSignAndSubmit = useCallback(async () => {
    if (!selectedSupervisorId) {
      notificationApi.warning({
        message: 'Missing Information',
        description: 'Please select a Supervisor',
      });
      return;
    }
    if (!selectedCustomerSupervisorId) {
      notificationApi.warning({
        message: 'Missing Information',
        description: 'Please select a Customer Supervisor',
      });
      return;
    }
    
    setSigning(true);
    try {
      const response = await api.post(`/api/reports/summary-service-report/${id}/sign-and-submit`, {
        supervisor_id: selectedSupervisorId,
        customer_supervisor_id: selectedCustomerSupervisorId
      });
      if (response.data.success) {
        notificationApi.success({
          message: 'Success',
          description: 'Report signed and submitted for approval',
        });
        setSignSubmitModalVisible(false);
        setSelectedSupervisorId(null);
        setSelectedCustomerSupervisorId(null);
        fetchReport();
      }
    } catch (error) {
      console.error('Error signing and submitting:', error);
      notificationApi.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to sign and submit',
      });
    } finally {
      setSigning(false);
    }
  }, [id, selectedSupervisorId, selectedCustomerSupervisorId, notificationApi, fetchReport]);

  // Supervisor sign handler
  const handleSupervisorSign = useCallback(async () => {
    const mockSignatureUrl = 'signature_placeholder.png';
    setSigning(true);
    try {
      const response = await api.post(`/api/reports/summary-service-report/${id}/supervisor-sign`, {
        signature_url: mockSignatureUrl
      });
      if (response.data.success) {
        notificationApi.success({
          message: 'Success',
          description: 'You have signed as Supervisor',
        });
        fetchReport();
      }
    } catch (error) {
      console.error('Error signing as supervisor:', error);
      notificationApi.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to sign',
      });
    } finally {
      setSigning(false);
    }
  }, [id, notificationApi, fetchReport]);

  // Customer sign handler
  const handleCustomerSign = useCallback(async () => {
    const mockSignatureUrl = 'signature_placeholder.png';
    setSigning(true);
    try {
      const response = await api.post(`/api/reports/summary-service-report/${id}/customer-sign`, {
        signature_url: mockSignatureUrl
      });
      if (response.data.success) {
        notificationApi.success({
          message: 'Success',
          description: 'You have approved as Customer Supervisor',
        });
        fetchReport();
      }
    } catch (error) {
      console.error('Error approving as customer:', error);
      notificationApi.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to approve',
      });
    } finally {
      setSigning(false);
    }
  }, [id, notificationApi, fetchReport]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // Memoize columns
  const columns = useMemo(() => [
    {
      title: 'Item',
      dataIndex: 'item_number',
      key: 'item_number',
      width: 60,
      fixed: 'left',
    },
    {
      title: 'Date',
      dataIndex: 'activity_date',
      key: 'activity_date',
      width: 110,
      render: (date) => dayjs(date).format('DD/MM/YYYY')
    },
    {
      title: 'Service Report No',
      dataIndex: 'service_report_no',
      key: 'service_report_no',
      width: 150,
      render: (text, record) => (
        <Input
          defaultValue={text || ''}
          placeholder="Enter report no"
          size="small"
          onBlur={(e) => handleCellChange(record.id, 'service_report_no', e.target.value)}
        />
      )
    },
    {
      title: 'Products',
      dataIndex: 'products',
      key: 'products',
      width: 200,
      render: (text, record) => {
        const selected = text ? text.split(' & ') : [];
        return (
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select products"
            size="small"
            value={selected}
            onChange={(values) => handleCellChange(record.id, 'products', values.join(' & '))}
            options={productSelectOptions}
            maxTagCount="responsive"
            maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
            popupMatchSelectWidth={false}
          />
        );
      }
    },
    {
      title: 'Service Type',
      dataIndex: 'service_types',
      key: 'service_types',
      width: 200,
      render: (text, record) => {
        const selected = text ? text.split(' & ') : [];
        return (
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select service types"
            size="small"
            value={selected}
            onChange={(values) => handleCellChange(record.id, 'service_types', values.join(' & '))}
            options={serviceTypeSelectOptions}
            maxTagCount="responsive"
            maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
            popupMatchSelectWidth={false}
          />
        );
      }
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 100,
      render: (text, record) => {
        const selected = text ? text.split(' & ') : [];
        const displayText = selected.map(getShortLocationName).join(' & ') || 'Select';
        
        return (
          <Popover
            trigger="click"
            content={
              <Checkbox.Group
                value={selected}
                onChange={(values) => handleCellChange(record.id, 'location', values.join(' & '))}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <Checkbox value="LHDNM Cyberjaya">LHDNM Cyberjaya (CBJ)</Checkbox>
                <Checkbox value="LHDNM Melaka">LHDNM Melaka (MLK)</Checkbox>
              </Checkbox.Group>
            }
            title="Select Locations"
          >
            <Tag 
              color="blue" 
              style={{ cursor: 'pointer', width: '100%', textAlign: 'center', margin: 0 }}
            >
              {displayText}
            </Tag>
          </Popover>
        );
      }
    },
    {
      title: 'Engineer(s)',
      dataIndex: 'engineer_name',
      key: 'engineer_name',
      width: 220,
      render: (text, record) => {
        const selected = text ? text.split(' & ') : [];
        return (
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select engineer(s)"
            size="small"
            value={selected}
            onChange={(values) => handleCellChange(record.id, 'engineer_name', values.join(' & '))}
            options={teamMemberOptions}
            showSearch
            optionFilterProp="label"
            allowClear
            maxTagCount="responsive"
            maxTagPlaceholder={(omitted) => `+${omitted.length}`}
            popupMatchSelectWidth={false}
          />
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Copy to next empty row">
            <Button 
              icon={<CopyOutlined />} 
              size="small"
              onClick={() => handleCopyRow(record.id)}
            />
          </Tooltip>
          <Tooltip title="Copy to ALL empty rows">
            <Popconfirm
              title="Copy to All Empty Rows"
              description="This will copy all values from this row to EVERY empty row in the report. Continue?"
              onConfirm={() => handleBulkCopy(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button 
                icon={<FileExcelOutlined />} 
                size="small"
                style={{ color: '#52c41a' }}
              />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Clear all values">
            <Popconfirm
              title="Clear Row"
              description="Are you sure you want to clear all values in this row? This action cannot be undone."
              onConfirm={() => handleClearRow(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button 
                danger
                icon={<DeleteOutlined />} 
                size="small"
              />
            </Popconfirm>
          </Tooltip>
          {/* Delete Row button */}
          <Tooltip title="Delete this day from report">
            <Popconfirm
              title="Delete Row"
              description="Are you sure you want to permanently delete this day? This action cannot be undone. You can re-add weekends later if needed."
              onConfirm={() => handleDeleteRow(record.id, record.item_number)}
              okText="Yes, Delete"
              cancelText="Cancel"
              okType="danger"
            >
              <Button 
                danger
                icon={<DeleteOutlined />} 
                size="small"
                style={{ backgroundColor: '#ff4d4f', color: 'white' }}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ], [handleCellChange, handleCopyRow, handleBulkCopy, handleClearRow, handleDeleteRow, teamMemberOptions]);

  const canEdit = report?.status === 'draft';
  const isPreparer = user.id === report?.prepared_by_user_id;
  const isAssignedSupervisor = user.id === report?.reviewed_by_user_id;
  const isAssignedCustomerSupervisor = user.id === report?.approved_by_user_id;
  
  const showSupervisorSign = report?.status === 'submitted' && 
                            report?.reviewed_by_user_id && 
                            !report?.reviewed_signed_at &&
                            isAssignedSupervisor;
  
  const showCustomerApprove = report?.status === 'submitted' && 
                              report?.approved_by_user_id && 
                              !report?.approved_signed_at &&
                              isAssignedCustomerSupervisor;
  
  // Preview button is visible to all signers (anyone who can access the report)
  const showPreviewButton = report !== null;

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="Loading report..." />
      </div>
    );
  }

  return (
    <div className="analytics-page">
      {/* Improved Header Section */}
      <div className="analytics-header">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/reports/summary-service-report')}
          style={{ marginBottom: 16 }}
        >
          Back
        </Button>
        
        <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
          <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                Summary Service Report
              </Title>
            </Col>
            <Col>
              <Tag 
                color={report?.status === 'approved' ? 'success' : report?.status === 'submitted' ? 'processing' : 'default'}
                icon={report?.status === 'approved' ? <CheckCircleOutlined /> : report?.status === 'submitted' ? <ClockCircleOutlined /> : <EditOutlined />}
              >
                {report?.status === 'draft' ? 'Draft' : report?.status === 'submitted' ? 'Pending Approval' : 'Approved'}
              </Tag>
            </Col>
          </Row>
          
          <Divider style={{ margin: '12px 0' }} />
          
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Report Identifier
            </Text>
            <div style={{ 
              backgroundColor: '#fafafa', 
              padding: '8px 12px', 
              borderRadius: 6,
              fontFamily: 'monospace',
              fontSize: 12,
              wordBreak: 'break-all',
              color: '#666'
            }}>
              {report?.report_uuid}
            </div>
          </div>
          
          <div style={{ marginTop: 12 }}>
            <Space split={<Divider type="vertical" />} wrap>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Contract</Text>
                <div style={{ fontWeight: 500 }}>{report?.contract_name}</div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Month</Text>
                <div style={{ fontWeight: 500 }}>{dayjs(report?.report_month).format('MMMM YYYY')}</div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Apps/Infra</Text>
                <div><Tag color="purple" style={{ margin: 0 }}>{report?.apps_or_infra}</Tag></div>
              </div>
              {report?.prepared_by_user_id && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Prepared By</Text>
                  <div style={{ fontWeight: 500 }}>{report?.prepared_by_name || 'N/A'}</div>
                </div>
              )}
            </Space>
          </div>
        </Card>
      </div>

      {/* Signature Status Panel */}
      <Card title="Signature Status" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          {/* 1. Preparer */}
          <Col xs={24} md={8}>
            <div style={{ 
              border: '1px solid #f0f0f0', 
              borderRadius: 8, 
              padding: 16,
              backgroundColor: report?.prepared_signed_at ? '#f6ffed' : '#fafafa'
            }}>
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong>1. Preparer</Text>
                  {report?.prepared_signed_at ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>Signed</Tag>
                  ) : (
                    <Tag color="default" icon={<ClockCircleOutlined />}>Pending</Tag>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>Report Creator</Text>
                <Text>{report?.prepared_by_name || 'Not assigned'}</Text>
                {report?.prepared_signed_at && (
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Signed: {dayjs(report?.prepared_signed_at).format('DD/MM/YYYY HH:mm')}
                  </div>
                )}
              </Space>
            </div>
          </Col>

          {/* 2. Supervisor */}
          <Col xs={24} md={8}>
            <div style={{ 
              border: '1px solid #f0f0f0', 
              borderRadius: 8, 
              padding: 16,
              backgroundColor: report?.reviewed_signed_at ? '#f6ffed' : '#fafafa'
            }}>
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong>2. Supervisor</Text>
                  {report?.reviewed_signed_at ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>Signed</Tag>
                  ) : report?.reviewed_by_user_id ? (
                    <Tag color="processing" icon={<ClockCircleOutlined />}>Pending</Tag>
                  ) : (
                    <Tag color="warning" icon={<ClockCircleOutlined />}>Not Assigned</Tag>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>Internal/Partner Supervisor</Text>
                <Text>{report?.reviewed_by_name || 'Not assigned'}</Text>
                {report?.reviewed_signed_at && (
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Signed: {dayjs(report?.reviewed_signed_at).format('DD/MM/YYYY HH:mm')}
                  </div>
                )}
              </Space>
            </div>
          </Col>

          {/* 3. Customer Supervisor */}
          <Col xs={24} md={8}>
            <div style={{ 
              border: '1px solid #f0f0f0', 
              borderRadius: 8, 
              padding: 16,
              backgroundColor: report?.approved_signed_at ? '#f6ffed' : '#fafafa'
            }}>
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong>3. Customer Supervisor</Text>
                  {report?.approved_signed_at ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>Approved</Tag>
                  ) : report?.approved_by_user_id ? (
                    <Tag color="processing" icon={<ClockCircleOutlined />}>Pending</Tag>
                  ) : (
                    <Tag color="warning" icon={<ClockCircleOutlined />}>Not Assigned</Tag>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>Customer Supervisor</Text>
                <Text>{report?.approved_by_name || 'Not assigned'}</Text>
                {report?.approved_signed_at && (
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Approved: {dayjs(report?.approved_signed_at).format('DD/MM/YYYY HH:mm')}
                  </div>
                )}
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Action Buttons */}
      <Card className="filter-card" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col>
            <Space>
              {selectedRowKeys.length > 0 && (
                <Button 
                  type="primary" 
                  onClick={() => setBatchModalVisible(true)}
                >
                  Batch Update ({selectedRowKeys.length} rows)
                </Button>
              )}
              {selectedRowKeys.length > 0 && (
                <Button onClick={() => setSelectedRowKeys([])}>
                  Clear Selection
                </Button>
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              {/* Preview Report button */}
              {showPreviewButton && (
                <Tooltip title="Preview Report">
                  <Button 
                    icon={<EyeOutlined />}
                    onClick={handlePreviewReport}
                    loading={previewing}
                    style={{ background: '#1890ff', borderColor: '#1890ff', color: 'white' }}
                  >
                    Preview
                  </Button>
                </Tooltip>
              )}
              
              {/* Download Dropdown Button */}
              <Dropdown menu={{ items: downloadMenuItems }} trigger={['click']} disabled={exporting}>
                <Button 
                  type="primary"
                  icon={<DownloadOutlined />}
                  loading={exporting}
                  style={{ background: '#52c41a', borderColor: '#52c41a' }}
                >
                  Download
                </Button>
              </Dropdown>
              
              {/* Add Weekends button - only visible in draft status */}
              {canEdit && (
                <Tooltip title="Add weekend days (Saturday/Sunday) to the report">
                  <Button 
                    icon={<CalendarOutlined />}
                    onClick={fetchMissingWeekends}
                    loading={loadingWeekends}
                    style={{ background: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                  >
                    Add Weekends
                  </Button>
                </Tooltip>
              )}
              
              {/* Sign & Submit button - only for preparer when report is draft and not yet signed */}
              {canEdit && isPreparer && !report?.prepared_signed_at && (
                <Button 
                  type="primary" 
                  icon={<SendOutlined />}
                  onClick={() => setSignSubmitModalVisible(true)}
                  size="large"
                >
                  Sign & Submit Report
                </Button>
              )}
              
              {/* Sign button for Supervisor - only when assigned and not yet signed */}
              {showSupervisorSign && (
                <Popconfirm
                  title="Sign as Supervisor"
                  description="Are you sure you want to sign this report as Supervisor?"
                  onConfirm={handleSupervisorSign}
                  okText="Yes, Sign"
                  cancelText="Cancel"
                >
                  <Button 
                    type="primary" 
                    icon={<SignatureOutlined />}
                    loading={signing}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    size="large"
                  >
                    Sign as Supervisor
                  </Button>
                </Popconfirm>
              )}
              
              {/* Approve button for Customer Supervisor - only when assigned and not yet approved */}
              {showCustomerApprove && (
                <Popconfirm
                  title="Approve Report"
                  description="Are you sure you want to approve this report as Customer Supervisor?"
                  onConfirm={handleCustomerSign}
                  okText="Yes, Approve"
                  cancelText="Cancel"
                >
                  <Button 
                    type="primary" 
                    icon={<CheckCircleOutlined />}
                    loading={signing}
                    style={{ background: '#722ed1', borderColor: '#722ed1' }}
                    size="large"
                  >
                    Approve as Customer Supervisor
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Editable Table */}
      <Card className="report-card">
        <Table
          columns={columns}
          dataSource={dailyEntries}
          rowKey="id"
          rowSelection={canEdit ? rowSelection : null}
          pagination={false}
          bordered
          size="middle"
          scroll={{ x: 1400 }}
          className="report-table"
        />
        
        <div className="report-footer">
          <Text type="secondary">
            {canEdit ? 'Click cells to edit. Use checkboxes to select multiple rows for batch update.' : 'Report is locked for editing.'}
          </Text>
        </div>
      </Card>

      {/* Batch Update Modal */}
      <Modal
        title="Batch Update Selected Rows"
        open={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        onOk={handleBatchUpdate}
        confirmLoading={saving}
        width={500}
      >
        <Form form={batchForm} layout="vertical">
          <Form.Item name="service_report_no" label="Service Report No">
            <Input placeholder="Leave blank to keep original" />
          </Form.Item>
          <Form.Item name="products" label="Products">
            <Select
              mode="multiple"
              placeholder="Select products"
              options={productSelectOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item name="service_types" label="Service Type">
            <Select
              mode="multiple"
              placeholder="Select service types"
              options={serviceTypeSelectOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Select
              mode="multiple"
              placeholder="Select locations"
              options={locationSelectOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item name="engineer_name" label="Engineer(s)">
            <Select
              mode="multiple"
              placeholder="Select engineer(s)"
              options={teamMemberOptions}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Sign & Submit Modal */}
      <Modal
        title="Sign & Submit Report"
        open={signSubmitModalVisible}
        onCancel={() => {
          setSignSubmitModalVisible(false);
          setSelectedSupervisorId(null);
          setSelectedCustomerSupervisorId(null);
        }}
        onOk={handleSignAndSubmit}
        confirmLoading={signing}
        okText="Confirm & Submit"
        cancelText="Cancel"
        width={500}
      >
        <Form layout="vertical">
          <Alert
            message="You are about to sign as Preparer"
            description="This action will lock the report and send it for approval."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Form.Item 
            label="Select Supervisor" 
            required
            tooltip="This person will review and sign the report. Only internal/partner users are shown."
          >
            <Select
              placeholder="Select supervisor"
              style={{ width: '100%' }}
              value={selectedSupervisorId}
              onChange={setSelectedSupervisorId}
              options={supervisorOptions}
              showSearch
              loading={loadingSupervisors}
              notFoundContent={loadingSupervisors ? "Loading..." : "No internal/partner users found"}
            />
          </Form.Item>

          <Form.Item 
            label="Select Customer Supervisor" 
            required
            tooltip="Only users with Customer Supervisor role will appear here"
          >
            <Select
              placeholder="Select customer supervisor"
              style={{ width: '100%' }}
              value={selectedCustomerSupervisorId}
              onChange={setSelectedCustomerSupervisorId}
              options={customerSupervisorOptions}
              showSearch
              loading={loadingCustomers}
              notFoundContent={loadingCustomers ? "Loading..." : "No customer supervisors found"}
            />
          </Form.Item>

          <Text type="secondary" style={{ fontSize: 12 }}>
            After submission, the report will be locked and cannot be edited.
          </Text>
        </Form>
      </Modal>

      {/* Add Weekends Modal */}
      <Modal
        title="Add Weekend Days"
        open={missingWeekendsModalVisible}
        onCancel={() => {
          setMissingWeekendsModalVisible(false);
          setSelectedWeekends([]);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setMissingWeekendsModalVisible(false);
            setSelectedWeekends([]);
          }}>
            Cancel
          </Button>,
          <Button 
            key="add" 
            type="primary" 
            onClick={handleAddWeekends}
            loading={loadingWeekends}
            disabled={selectedWeekends.length === 0}
            style={{ backgroundColor: '#52c41a' }}
          >
            Add Selected ({selectedWeekends.length})
          </Button>
        ]}
        width={500}
      >
        <Alert
          message="Weekend Days"
          description="Select the weekend days you want to add to the report. They will be inserted in the correct chronological order."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {missingWeekends.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">No missing weekends found for this month.</Text>
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <div style={{ marginBottom: 8 }}>
              <Checkbox
                indeterminate={selectedWeekends.length > 0 && selectedWeekends.length < missingWeekends.length}
                checked={selectedWeekends.length === missingWeekends.length && missingWeekends.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedWeekends(missingWeekends.map(w => w.date));
                  } else {
                    setSelectedWeekends([]);
                  }
                }}
              >
                Select All
              </Checkbox>
            </div>
            <Divider style={{ margin: '8px 0' }} />
            {missingWeekends.map(weekend => (
              <div key={weekend.date} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Checkbox
                  checked={selectedWeekends.includes(weekend.date)}
                  onChange={(e) => handleWeekendSelect(weekend.date, e.target.checked)}
                >
                  <span style={{ fontWeight: weekend.dayName === 'Sunday' ? 'bold' : 'normal' }}>
                    {weekend.dayName}, {weekend.displayDate}
                  </span>
                </Checkbox>
              </div>
            ))}
          </div>
        )}
        
        <div style={{ marginTop: 16, padding: '8px', background: '#f6ffed', borderRadius: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            Added weekends will appear in the correct order and you can edit them like any other row.
          </Text>
        </div>
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        title={pdfPreviewTitle || 'PDF Preview'}
        open={previewModalVisible}
        onCancel={handlePreviewModalClose}
        footer={[
          <Button key="close" onClick={handlePreviewModalClose}>Close</Button>,
          <Button 
            key="download" 
            type="primary" 
            onClick={() => {
              if (pdfPreviewBuffer) {
                const blob = new Blob([pdfPreviewBuffer], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${pdfPreviewTitle || 'report'}.pdf`;
                link.click();
                URL.revokeObjectURL(url);
              }
            }}
          >
            Download PDF
          </Button>
        ]}
        width="90%"
        style={{ top: 20 }}
        styles={{ body: { height: 'calc(100vh - 200px)', padding: 0 } }}
      >
        {pdfPreviewBuffer && (
          <iframe
            src={URL.createObjectURL(new Blob([pdfPreviewBuffer], { type: 'application/pdf' }))}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="PDF Preview"
          />
        )}
      </Modal>
    </div>
  );
};

export default React.memo(SummaryServiceReportEdit);